import {
  faArchive,
  faArrowUpFromSquare,
  faChain,
  faCopy,
  faEllipsisH,
  faGlobe,
  faPen,
  faRocket,
  faShare,
  faShareAltSquare,
  faTimes,
  faTrash,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { get } from "lodash";
import { PropsWithChildren, useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Button, Divider, Popup } from "semantic-ui-react";
import { useIdentity } from "../../../../../../../../hooks/use-identity";
import { LeftArrow } from "../../../../../../../global/components/icons";
import useSite from "../../../../../../../permits/components/inspections/InspectionPanel/useSite";
import { useNotifyContentDetails } from "../../../../../../hooks/useNotifyContent";
import { useNotifyNotificationContentArchive } from "../../../../../../hooks/useNotifyNotificationContentArchive";
import { useNotifyNotificationContentDeactivate } from "../../../../../../hooks/useNotifyNotificationContentDeactivate";
import { useNotifyRolesForPerson } from "../../../../../../hooks/useNotifyRolesForPerson";
import { NotifyNotificationsStatusLabelType } from "../components/NotifyNotificationsStatusLabel";
import { NotifyNotificationsActionMode, useNotifyNotificationsActions } from "../hooks/useNotifyNotificationsActions";
import { ChannelIcon } from "./NotifyNotificationsChannelIcon";

function DropdownTab({
  children,
  onClick = () => {},
  className,
  danger = false,
  active = false,
  disabled = false,
  href,
  external = false,
}: PropsWithChildren<{
  onClick?: (e?: React.MouseEvent) => void;
  className?: string;
  danger?: boolean;
  active?: boolean;
  disabled?: boolean;
  href?: string;
  external?: boolean;
}>) {
  const getLinkProps = () => {
    if (href && external) {
      return { as: Link, to: { pathname: href }, target: "_blank" };
    }
    if (href) {
      return { as: Link, to: href };
    }
    return {};
  };

  return (
    <Button
      {...getLinkProps()}
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        "tw-m-0 tw-w-full tw-rounded-sm tw-border-none tw-p-gg-2 tw-font-semibold",
        {
          "tw-bg-white tw-text-text-secondary hover:tw-bg-grey-95": !danger && !active,
          "tw-bg-white tw-text-red hover:tw-bg-error-95": danger,
          "tw-bg-info-background tw-text-primary tw-ring-1 tw-ring-primary": active,
        },
        className,
      )}
    >
      {children}
    </Button>
  );
}

const NotifyPermissionsRole = {
  Publisher: "Publisher",
  Drafter: "Drafter",
} as const;
type NotifyPermissionsRole = (typeof NotifyPermissionsRole)[keyof typeof NotifyPermissionsRole];

function useNotifyPermissionsForLoggedInUser() {
  const { user } = useIdentity();
  const { notifyRoles } = useNotifyRolesForPerson({
    personId: user?.id,
  });

  const systemRole = get(user, "communications_role", "communications_none") as
    | "communications_admin"
    | "communications_user"
    | "communications_none";

  const isCommunicationsAdmin = systemRole === "communications_admin";

  const getRoleForDepartment = useCallback(
    (departmentId: number) => {
      if (isCommunicationsAdmin) return NotifyPermissionsRole.Publisher;
      const notifyRole = notifyRoles?.find((role) => role.departmentId === departmentId) || null;
      if (!notifyRole) return null;
      return notifyRole.role;
    },
    [isCommunicationsAdmin, notifyRoles],
  );

  return {
    getRoleForDepartment,
  };
}

const NotifyNotificationQuickActionsAction = {
  Share: "share",
  PublicView: "public-view",
  Archive: "archive",
  SendNow: "send-now",
  Edit: "edit",
  Duplicate: "duplicate",
  CopyReviewLink: "copy-review-link",
  Delete: "delete",
  CancelSend: "cancel-send",
} as const;
type NotifyNotificationQuickActionsAction =
  (typeof NotifyNotificationQuickActionsAction)[keyof typeof NotifyNotificationQuickActionsAction];

const allowedActionsLookup: Record<
  "Publisher" | "Drafter",
  Record<NotifyNotificationsStatusLabelType, Set<NotifyNotificationQuickActionsAction>>
> = {
  Publisher: {
    Draft: new Set([
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
      NotifyNotificationQuickActionsAction.Delete,
    ]),
    Rejected: new Set([
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
      NotifyNotificationQuickActionsAction.Delete,
    ]),
    Pending: new Set([
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
      NotifyNotificationQuickActionsAction.Delete,
    ]),
    Scheduled: new Set([
      NotifyNotificationQuickActionsAction.Archive,
      NotifyNotificationQuickActionsAction.SendNow,
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
    ]),
    Sending: new Set([
      NotifyNotificationQuickActionsAction.PublicView,
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
      NotifyNotificationQuickActionsAction.CancelSend,
    ]),
    Active: new Set([
      NotifyNotificationQuickActionsAction.Share,
      NotifyNotificationQuickActionsAction.PublicView,
      NotifyNotificationQuickActionsAction.Archive,
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
    ]),
    Archived: new Set([NotifyNotificationQuickActionsAction.Duplicate, NotifyNotificationQuickActionsAction.Delete]),
  },
  Drafter: {
    Draft: new Set([
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
      NotifyNotificationQuickActionsAction.Delete,
    ]),
    Rejected: new Set([
      NotifyNotificationQuickActionsAction.Edit,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
      NotifyNotificationQuickActionsAction.Delete,
    ]),
    Pending: new Set([
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
    ]),
    Scheduled: new Set([
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
    ]),
    Sending: new Set([
      NotifyNotificationQuickActionsAction.PublicView,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
    ]),
    Active: new Set([
      NotifyNotificationQuickActionsAction.PublicView,
      NotifyNotificationQuickActionsAction.Duplicate,
      NotifyNotificationQuickActionsAction.CopyReviewLink,
    ]),
    Archived: new Set([NotifyNotificationQuickActionsAction.Duplicate, NotifyNotificationQuickActionsAction.Delete]),
  },
};

export function NotifyNotificationQuickActions({
  content: partialContent,
  hideEditAction = false,
}: {
  content: {
    id: number;
    status: NotifyNotificationsStatusLabelType;
    departmentSentFrom: { departmentId: number } | { id: number } | null;
    headline: string;
  };
  hideEditAction?: boolean;
}) {
  const site = useSite();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const sharePopupRef = useRef<any>(null);
  const [isSharePopupOpen, setIsSharePopupOpen] = useState(false);
  const { getRoleForDepartment } = useNotifyPermissionsForLoggedInUser();
  const { addToast } = useToasts();

  const { onSend, isSendLoading, convertContentToFormData } = useNotifyNotificationsActions({
    setStep: () => {},
    mode: NotifyNotificationsActionMode.Edit,
    notifyContentId: partialContent.id,
  });
  const { archiveNotificationContent, isArchiveNotificationContentLoading } = useNotifyNotificationContentArchive();
  const { deactivateNotificationContent, isDeactivateNotificationContentLoading } =
    useNotifyNotificationContentDeactivate();

  const { notifyContent, areContentDetailsLoading } = useNotifyContentDetails({
    id: partialContent.id,
    enabled: isPopupOpen && partialContent.status === "Scheduled",
  });

  const onClosePopup = () => {
    setIsPopupOpen(false);
    setIsSharePopupOpen(false);
  };

  const onCopyToClipboard = async (text: string, successMessage: string) => {
    try {
      await navigator.clipboard.writeText(text);
      addToast(successMessage, { appearance: "success", autoDismiss: true });
    } catch (error) {
      addToast("Failed to copy to clipboard", { appearance: "error", autoDismiss: true });
    }
    onClosePopup();
  };

  const onNativeShare = async (text: string) => {
    try {
      if (navigator.share) {
        await navigator.share({ text });
      } else {
        throw new Error("Web Share API not supported");
      }
    } catch (error) {
      throw new Error("Failed to share content");
    } finally {
      onClosePopup();
    }
  };

  const onSendNow = async () => {
    if (!notifyContent) return;
    await onSend(convertContentToFormData(notifyContent));
    addToast("Sending notification", { appearance: "success", autoDismiss: true });
  };

  const onDeactivate = async () => {
    await deactivateNotificationContent(partialContent.id);
    addToast("Notification deleted", { appearance: "success", autoDismiss: true });
    onClosePopup();
  };

  const onArchive = async () => {
    await archiveNotificationContent(partialContent.id);
    addToast("Notification archived", { appearance: "success", autoDismiss: true });
    onClosePopup();
  };

  // The id is named differently depending on whether it comes from elasticsearch or the core service
  const departmentSentFromId =
    partialContent.departmentSentFrom &&
    ((partialContent.departmentSentFrom as any).departmentId ?? (partialContent.departmentSentFrom as any).id);
  const notifyRoleForDepartment = partialContent.departmentSentFrom ? getRoleForDepartment(departmentSentFromId) : null;
  const allowedActions = allowedActionsLookup[notifyRoleForDepartment ?? "Drafter"][partialContent.status];

  const hasAnyDangerousActions =
    allowedActions.has(NotifyNotificationQuickActionsAction.Delete) ||
    allowedActions.has(NotifyNotificationQuickActionsAction.CancelSend);

  const publicViewUrl = `/${site}/public/notify/posts/${partialContent.id}`;
  const fullPublicViewUrl = `${window.location.origin}${publicViewUrl}`;
  const duplicateUrl = `/${site}/notify/notifications/create?template-type=notification&template-id=${partialContent.id}`;
  const editUrl = `/${site}/notify/notifications/${partialContent.id}?step=edit`;
  const reviewUrl = `/${site}/notify/notifications/${partialContent.id}?step=review`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(fullPublicViewUrl)}&quote=${partialContent.headline}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(fullPublicViewUrl)}&text=${partialContent.headline}`;

  return (
    <Popup
      trigger={
        <Button className='button-ghost black icon leave-me-alone tw-z-50 tw-m-0'>
          <FontAwesomeIcon icon={faEllipsisH} />
        </Button>
      }
      basic
      on='click'
      position='bottom left'
      offset={[0, 4]}
      closeOnTriggerMouseLeave={false}
      className='leave-me-alone tw-m-0 tw-min-w-44 tw-p-gg-2 tw-shadow-md'
      open={isPopupOpen}
      onOpen={() => setIsPopupOpen(true)}
      onClose={(e) => {
        const clickTargetIsSharePopup = sharePopupRef?.current?.elementRef?.current?.contains(e.target as Node);
        if (clickTargetIsSharePopup) return;
        onClosePopup();
      }}
    >
      <div className='tw-flex tw-flex-col tw-gap-gg-1'>
        {allowedActions.has(NotifyNotificationQuickActionsAction.Share) && (
          <Popup
            ref={sharePopupRef}
            trigger={
              <DropdownTab className='tw-flex tw-gap-gg-2' active={isSharePopupOpen}>
                <FontAwesomeIcon icon={faShareAltSquare} />
                Share
                <LeftArrow />
              </DropdownTab>
            }
            basic
            on='click'
            position='right center'
            offset={[53, 4]} // Magic numbers to align the sub-popup properly
            closeOnTriggerMouseLeave={false}
            className='leave-me-alone tw-min-w-44 tw-p-gg-2 tw-shadow-md'
            open={isSharePopupOpen}
            onOpen={() => setIsSharePopupOpen(true)}
            onClose={() => setIsSharePopupOpen(false)}
          >
            <div className='tw-flex tw-flex-col tw-gap-gg-1'>
              <DropdownTab
                className='tw-flex tw-gap-gg-2'
                onClick={() => onCopyToClipboard(publicViewUrl, "Web link copied to clipboard")}
              >
                <FontAwesomeIcon icon={faChain} />
                Copy Web Link
              </DropdownTab>
              <DropdownTab href={facebookShareUrl} external className='tw-flex tw-h-gg-6 tw-items-center tw-gap-gg-2'>
                <ChannelIcon channel='Facebook' isStaticIcon className='-tw-ml-[2px] tw-scale-90' />
                Share on Facebook
              </DropdownTab>
              <DropdownTab href={twitterShareUrl} external className='tw-flex tw-h-gg-6 tw-items-center tw-gap-gg-2'>
                <ChannelIcon channel='Twitter' isStaticIcon className='-tw-ml-[2px] tw-scale-90' />
                Share on Twitter
              </DropdownTab>
              <DropdownTab className='tw-flex tw-gap-gg-2' onClick={() => onNativeShare(publicViewUrl)}>
                <FontAwesomeIcon icon={faArrowUpFromSquare} />
                Share
              </DropdownTab>
            </div>
          </Popup>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.PublicView) && (
          <DropdownTab href={publicViewUrl} className='tw-flex tw-gap-gg-2'>
            <FontAwesomeIcon icon={faGlobe} />
            Public View
          </DropdownTab>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.Archive) && (
          <DropdownTab
            className='tw-flex tw-gap-gg-2'
            disabled={isArchiveNotificationContentLoading}
            onClick={onArchive}
          >
            <FontAwesomeIcon icon={faArchive} />
            Archive
          </DropdownTab>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.SendNow) && (
          <DropdownTab
            className='tw-flex tw-gap-gg-2'
            disabled={isSendLoading || areContentDetailsLoading}
            onClick={onSendNow}
          >
            <FontAwesomeIcon icon={faRocket} />
            Send Now
          </DropdownTab>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.Edit) && !hideEditAction && (
          <DropdownTab href={editUrl} className='tw-flex tw-gap-gg-2'>
            <FontAwesomeIcon icon={faPen} />
            Edit
          </DropdownTab>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.Duplicate) && (
          <DropdownTab href={duplicateUrl} className='tw-flex tw-gap-gg-2'>
            <FontAwesomeIcon icon={faCopy} />
            Duplicate
          </DropdownTab>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.CopyReviewLink) && (
          <DropdownTab
            className='tw-flex tw-gap-gg-2'
            onClick={() => onCopyToClipboard(reviewUrl, "Review link copied to clipboard")}
          >
            <FontAwesomeIcon icon={faShare} />
            Copy Review Link
          </DropdownTab>
        )}
        {hasAnyDangerousActions && <Divider className='tw-m-0' />}
        {allowedActions.has(NotifyNotificationQuickActionsAction.Delete) && (
          <DropdownTab
            className='tw-flex tw-gap-gg-2'
            danger
            disabled={isDeactivateNotificationContentLoading}
            onClick={onDeactivate}
          >
            <FontAwesomeIcon icon={faTrash} />
            Delete
          </DropdownTab>
        )}
        {allowedActions.has(NotifyNotificationQuickActionsAction.CancelSend) && (
          <DropdownTab className='tw-flex tw-gap-gg-2' danger disabled>
            <FontAwesomeIcon icon={faTimes} />
            Cancel Send
          </DropdownTab>
        )}
      </div>
    </Popup>
  );
}
